import React, { useEffect, useState } from 'react';
import {
  Form,
  Input,
  InputNumber,
  DatePicker,
  Select,
  Switch,
  Upload,
  Button,
  Image,
  App,
} from 'antd';
import { UploadOutlined, PictureOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

// Genera un formulario de atributos a partir del catálogo (component_attribute_def).
// values = objeto attributes actual; onChange(nextAttributes) en cada cambio.
// readOnly = true deshabilita todos los controles (solo vista).
// imageApi (opcional) = { upload(attrKey, file) => Promise<key>, getUrl(attrKey) => Promise<url> }
//   — se pasa solo cuando hay contexto de instancia (equipo + nodo). En plantillas se omite.
function AttributeForm({ defs = [], values = {}, onChange, readOnly = false, imageApi = null }) {
  const emit = (key, val) => {
    const next = { ...values };
    if (val === undefined || val === null || val === '') delete next[key];
    else next[key] = val;
    onChange?.(next);
  };

  const renderControl = (def) => {
    const v = values[def.key];
    switch (def.data_type) {
      case 'number':
        return (
          <InputNumber
            id={def.key}
            style={{ width: '100%' }}
            value={v ?? null}
            onChange={(n) => emit(def.key, n)}
            disabled={readOnly}
          />
        );
      case 'bool':
        return (
          <Switch
            id={def.key}
            checked={!!v}
            onChange={(c) => emit(def.key, c)}
            disabled={readOnly}
          />
        );
      case 'date':
        return (
          <DatePicker
            id={def.key}
            style={{ width: '100%' }}
            value={v ? dayjs(v) : null}
            onChange={(d) => emit(def.key, d ? d.format('YYYY-MM-DD') : null)}
            disabled={readOnly}
          />
        );
      case 'select':
        return (
          <Select
            id={def.key}
            allowClear
            style={{ width: '100%' }}
            value={v ?? null}
            onChange={(s) => emit(def.key, s)}
            options={(def.options || []).map((o) => ({ value: o, label: o }))}
            disabled={readOnly}
          />
        );
      case 'image':
        return (
          <ImageControl
            attrKey={def.key}
            value={v}
            imageApi={imageApi}
            readOnly={readOnly}
            onKey={(key) => emit(def.key, key)}
          />
        );
      default:
        return (
          <Input
            id={def.key}
            value={v ?? ''}
            onChange={(e) => emit(def.key, e.target.value)}
            disabled={readOnly}
          />
        );
    }
  };

  return (
    <Form layout="vertical">
      {defs.map((def) => (
        <Form.Item key={def.key} label={<label htmlFor={def.key}>{def.label}</label>}>
          {renderControl(def)}
        </Form.Item>
      ))}
    </Form>
  );
}

// Control interno para data_type:'image'.
// Separado como componente para poder manejar estado de preview y uploading sin ensuciar AttributeForm.
function ImageControl({ attrKey, value, imageApi, readOnly, onKey }) {
  const { message } = App.useApp();
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  // Cargar preview cuando hay valor e imageApi disponible.
  useEffect(() => {
    let cancelled = false;
    if (value && imageApi) {
      imageApi
        .getUrl(attrKey)
        .then((url) => {
          if (!cancelled) setPreviewUrl(url);
        })
        .catch(() => {
          if (!cancelled) setPreviewUrl(null);
        });
    } else {
      setPreviewUrl(null);
    }
    return () => {
      cancelled = true;
    };
  }, [value, imageApi, attrKey]);

  const handleUpload = async (file) => {
    if (!imageApi) return false;
    setUploading(true);
    try {
      const key = await imageApi.upload(attrKey, file);
      onKey(key);
      // Refrescar preview con la nueva key ya persistida.
      try {
        const url = await imageApi.getUrl(attrKey);
        setPreviewUrl(url);
      } catch {
        // preview fallará en test; no crítico
      }
    } catch {
      message.error('No se pudo subir la imagen');
    } finally {
      setUploading(false);
    }
    // Retornar false para que antd NO suba por su cuenta.
    return false;
  };

  // Sin imageApi o en readOnly sin imageApi: placeholder mudo o raw key.
  if (!imageApi) {
    return (
      <span style={{ opacity: 0.5, fontSize: 13 }}>
        <PictureOutlined style={{ marginRight: 4 }} />
        {value || 'Imagen'}
      </span>
    );
  }

  return (
    <div>
      {previewUrl && (
        <div style={{ marginBottom: 8 }}>
          <Image
            src={previewUrl}
            width={120}
            height={90}
            style={{ objectFit: 'cover', borderRadius: 4 }}
            alt={attrKey}
          />
        </div>
      )}
      {!readOnly && (
        <Upload
          accept="image/*"
          showUploadList={false}
          beforeUpload={handleUpload}
          disabled={uploading}
        >
          <Button icon={<UploadOutlined />} loading={uploading} size="small">
            {value ? 'Cambiar imagen' : 'Subir imagen'}
          </Button>
        </Upload>
      )}
      {readOnly && !previewUrl && value && (
        <span style={{ opacity: 0.5, fontSize: 13 }}>
          <PictureOutlined style={{ marginRight: 4 }} />
          {value}
        </span>
      )}
    </div>
  );
}

export default AttributeForm;
